import { getVideoCoverImage } from 'lib/media/videoHelper';

export type VideoProvider = 'youtube' | 'vimeo';

/**
 * What an address typed into a link field is: a video (at its canonical address, the one
 * Cloudinary finds a poster for), an address naming a picture, or one that only loading it can
 * tell (a picture served without an extension, a web page).
 */
export type MediaLinkKind =
  | { type: 'notAUrl' }
  | { type: 'video'; provider: VideoProvider; url: string; poster: string }
  | { type: 'image'; url: string; extension: string }
  | { type: 'unknown'; url: string };

function parseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function videoOf(url: URL): { provider: VideoProvider; url: string } | null {
  const host = url.hostname.replace(/^(www|m|player)\./, '');
  const [first, second] = url.pathname.split('/').filter(Boolean);
  const youtubeId =
    host === 'youtu.be'
      ? first
      : host === 'youtube.com'
        ? first === 'watch'
          ? url.searchParams.get('v')
          : ['shorts', 'embed', 'live'].includes(first ?? '')
            ? second
            : undefined
        : undefined;
  if (youtubeId) {
    return {
      provider: 'youtube',
      url: `https://www.youtube.com/watch?v=${youtubeId}`
    };
  }
  const vimeoId =
    host === 'vimeo.com' ? (first === 'video' ? second : first) : undefined;
  if (vimeoId && /^\d+$/.test(vimeoId)) {
    return { provider: 'vimeo', url: `https://vimeo.com/${vimeoId}` };
  }
  return null;
}

const imageExtension = /\.(jpe?g|png|gif|webp|avif|bmp|heic|tiff?)$/i;

export const mediaLinkUtils = {
  /** An address without a scheme is read as https. */
  classify: (input: string): MediaLinkKind => {
    const parsed = parseUrl(
      /^https?:\/\//i.test(input) ? input : `https://${input}`
    );
    if (
      parsed === null ||
      !(parsed.hostname.includes('.') || parsed.hostname === 'localhost')
    ) {
      return { type: 'notAUrl' };
    }
    const video = videoOf(parsed);
    if (video) {
      return {
        type: 'video',
        provider: video.provider,
        url: video.url,
        poster: getVideoCoverImage(video.url)
      };
    }
    const extension = imageExtension.exec(parsed.pathname)?.[1]?.toLowerCase();
    return extension === undefined
      ? { type: 'unknown', url: parsed.href }
      : { type: 'image', url: parsed.href, extension };
  }
};
